import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import ReservationChecklist from '../components/ReservationChecklist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProposalsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [incomingProposals, setIncomingProposals] = useState([]);
  const [outgoingProposals, setOutgoingProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('incoming');
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProposals();
    }
  }, [user]);

  const fetchProposals = async () => {
    const token = localStorage.getItem('token');
    try {
      const [incomingRes, outgoingRes] = await Promise.all([
        axios.get(`${API}/proposals/incoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/proposals/outgoing`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setIncomingProposals(incomingRes.data);
      setOutgoingProposals(outgoingRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast({
        title: "Error",
        description: "Failed to load proposals",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleAccept = async (proposalId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/proposals/${proposalId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "✅ Proposal Accepted",
        description: "The proposal has been accepted successfully."
      });
      
      fetchProposals();
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to accept proposal",
        variant: "destructive"
      });
    }
  };

  const handleReject = async (proposalId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/proposals/${proposalId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Proposal Rejected",
        description: "The proposal has been declined."
      });
      
      setRejectingId(null);
      setRejectReason('');
      fetchProposals();
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject proposal",
        variant: "destructive"
      });
    }
  };

  const handleCancel = async (proposalId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(
        `${API}/proposals/${proposalId}/cancel?reason=${encodeURIComponent(cancelReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: "Proposal Cancelled",
        description: "The proposal has been cancelled."
      });
      
      setCancellingId(null);
      setCancelReason('');
      fetchProposals();
    } catch (error) {
      console.error('Error cancelling proposal:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to cancel proposal",
        variant: "destructive"
      });
    }
  };

  const viewConversation = (threadId) => {
    navigate(`/messages?thread=${threadId}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-sn-navy">Resource Proposals</h1>
          <p className="mt-2 text-gray-600">
            Manage resource proposals - RMs propose resources for PM projects
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('incoming')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'incoming'
                  ? 'border-sn-green text-sn-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Incoming Proposals
              {incomingProposals.length > 0 && (
                <span className="ml-2 bg-sn-green text-white rounded-full px-2 py-0.5 text-xs">
                  {incomingProposals.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('outgoing')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'outgoing'
                  ? 'border-sn-green text-sn-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Sent Proposals
              {outgoingProposals.length > 0 && (
                <span className="ml-2 bg-gray-500 text-white rounded-full px-2 py-0.5 text-xs">
                  {outgoingProposals.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-sn-green"></div>
            <p className="mt-2 text-gray-600">Loading proposals...</p>
          </div>
        ) : (
          <>
            {/* Incoming Proposals - PM receives */}
            {activeTab === 'incoming' && (
              <div className="space-y-4">
                {incomingProposals.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-500">No incoming proposals</p>
                  </div>
                ) : (
                  incomingProposals.map(({ proposal, resource, project, from_company }) => (
                    <div key={proposal.id} className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-sn-navy">
                              {resource?.name || 'Resource'} → {project?.title || 'Project'}
                            </h3>
                            {getStatusBadge(proposal.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-gray-600">Proposed Resource:</p>
                              <p className="font-medium">{resource?.role} - {resource?.seniority}</p>
                              <p className="text-gray-500">{resource?.city}, {resource?.country}</p>
                              <p className="text-gray-500">By: {from_company?.name}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">For Your Project:</p>
                              <p className="font-medium">{project?.title}</p>
                              <p className="text-gray-500">{project?.city}, {project?.country}</p>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-4">
                            Proposed: {new Date(proposal.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 flex items-center space-x-3">
                        {proposal.status === 'pending' ? (
                          <>
                            <button
                              onClick={() => handleAccept(proposal.id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                            >
                              ✓ Accept
                            </button>
                            
                            {rejectingId === proposal.id ? (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="text"
                                  placeholder="Reason for rejection (optional)"
                                  value={rejectReason}
                                  onChange={(e) => setRejectReason(e.target.value)}
                                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => handleReject(proposal.id)}
                                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                                >
                                  Confirm Reject
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectingId(null);
                                    setRejectReason('');
                                  }}
                                  className="text-gray-600 px-3 py-2 hover:text-gray-800"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setRejectingId(proposal.id)}
                                className="border border-red-600 text-red-600 px-4 py-2 rounded-md hover:bg-red-50 transition-colors"
                              >
                                ✗ Reject
                              </button>
                            )}
                          </>
                        ) : null}
                        
                        <button
                          onClick={() => viewConversation(proposal.conversation_thread_id)}
                          className="ml-auto border border-sn-green text-sn-green px-4 py-2 rounded-md hover:bg-green-50 transition-colors"
                        >
                          💬 View Conversation
                        </button>
                      </div>

                      {/* Show checklist if accepted */}
                      {proposal.status === 'accepted' && (
                        <ReservationChecklist reservationId={proposal.id} />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Outgoing Proposals - RM sent */}
            {activeTab === 'outgoing' && (
              <div className="space-y-4">
                {outgoingProposals.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 text-center">
                    <p className="text-gray-500">No sent proposals</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Go to Matching page to propose your resources for projects
                    </p>
                  </div>
                ) : (
                  outgoingProposals.map(({ proposal, resource, project, to_company }) => (
                    <div key={proposal.id} className="bg-white rounded-lg shadow-md p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-sn-navy">
                              {resource?.name || 'Resource'} → {project?.title || 'Project'}
                            </h3>
                            {getStatusBadge(proposal.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                            <div>
                              <p className="text-gray-600">Your Resource:</p>
                              <p className="font-medium">{resource?.role} - {resource?.seniority}</p>
                              <p className="text-gray-500">{resource?.city}, {resource?.country}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Proposed For:</p>
                              <p className="font-medium">{project?.title}</p>
                              <p className="text-gray-500">To: {to_company?.name}</p>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-4">
                            Proposed: {new Date(proposal.created_at).toLocaleString()}
                            {proposal.updated_at && ` • Updated: ${new Date(proposal.updated_at).toLocaleString()}`}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 flex items-center space-x-3">
                        {proposal.status === 'pending' && (
                          <>
                            {cancellingId === proposal.id ? (
                              <div className="flex items-center space-x-2 flex-1">
                                <input
                                  type="text"
                                  placeholder="Reason for cancellation (optional)"
                                  value={cancelReason}
                                  onChange={(e) => setCancelReason(e.target.value)}
                                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                                />
                                <button
                                  onClick={() => handleCancel(proposal.id)}
                                  className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                                >
                                  Confirm Cancel
                                </button>
                                <button
                                  onClick={() => {
                                    setCancellingId(null);
                                    setCancelReason('');
                                  }}
                                  className="text-gray-600 px-3 py-2 hover:text-gray-800"
                                >
                                  Back
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setCancellingId(proposal.id)}
                                className="border border-gray-600 text-gray-600 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
                              >
                                🚫 Cancel Proposal
                              </button>
                            )}
                          </>
                        )}
                        
                        <button
                          onClick={() => viewConversation(proposal.conversation_thread_id)}
                          className="ml-auto border border-sn-green text-sn-green px-4 py-2 rounded-md hover:bg-green-50 transition-colors"
                        >
                          💬 View Conversation
                        </button>
                      </div>

                      {/* Show checklist if accepted */}
                      {proposal.status === 'accepted' && (
                        <ReservationChecklist reservationId={proposal.id} />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProposalsPage;
